import React from 'react';
import styled from 'styled-components';
import is from 'styled-is';
import { observer } from 'mobx-react';
import colors from '@honeybook/hbui/dist/styles/colors';
import ArrowIcon from '@honeybook/icons/dist/icons/ArrowRight16';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import HBTooltip from '@honeybook/hbui/dist/components/HBTooltip/HBTooltip';
import spacing from '@honeybook/hbui/dist/styles/spacing';
import { truncatedText } from '@honeybook/hbui/dist/styles/mixins';
import ClipIcon from '@honeybook/icons/dist/icons/Clip16';
import Pencil16 from '@honeybook/icons/dist/icons/Pencil16';
import { EDIT_AUTOMATION_TOOLTIP } from '../StepSideBar/StepSideBar.styles';
import useAnalytics from '../../../../../common/useAnalytics';
import { AnalyticsEvents } from '../../../../../services/analytics/analytics-events';
import { AutomationCombinedTypes } from '../../../AutomationsEnums';
import { useRootStore } from '~/stores/useRootStore';

interface TemplateSelectButtonProps {
    onButtonClick: () => void;
    title: string;
    value: string;
    isEmailWithAttachments?: boolean;
    type: AutomationCombinedTypes;
}

const TemplateSelectButton = ({
    onButtonClick,
    title,
    value,
    isEmailWithAttachments,
    type
}: TemplateSelectButtonProps) => {
    const {
        automationUiStore: {
            isCreate,
            isApplied,
            tooltipCommonProps,
            isShowErrors,
            isSelectedStepEditingDisabled,
            selectedStep: { isDone, combinedType, email, emailSettings },
            openEmailEditor
        }
    } = useRootStore();

    const { track } = useAnalytics();

    const hasSelection = Boolean(value);

    const hasError = isShowErrors && !hasSelection;

    const handleButtonClick = () => {
        track(AnalyticsEvents.AutomationsSideBarOpenActionDropdown, {
            action: 'click',
            origin: combinedType,
            type
        });
        onButtonClick();
    };

    const previewEmail = () => {
        openEmailEditor('previewFromSidebar', email, emailSettings);
        track(AnalyticsEvents.AutomationPreviewEmail, {
            action: 'click',
            type: 'preview and edit'
        });
    };

    return (
        <ButtonContainer>
            <ButtonLabel hasError={hasError} type="Body6DarkPrimary">
                {title}
                {type === 'email' && !!email && (
                    <PreviewEdit
                        clickable
                        onClick={previewEmail}
                        type="LinkSecondary6DarkSemibold"
                    >
                        Preview
                        {!isDone && !isApplied && ' & edit'}
                    </PreviewEdit>
                )}
            </ButtonLabel>
            <HBTooltip
                {...tooltipCommonProps}
                target={
                    <SubMenuSelectButton
                        isDone={isDone}
                        isActive={hasSelection}
                        onClick={handleButtonClick}
                        hasError={hasError}
                        disabled={isSelectedStepEditingDisabled}
                    >
                        <SubMenuSelectButtonText type="Body6DarkPrimary">
                            {isEmailWithAttachments && (
                                <ClipIcon color={colors.neutral600} />
                            )}
                            {value || 'Select'}
                        </SubMenuSelectButtonText>
                        {isCreate ? (
                            <ArrowIcon color={colors.neutral800} />
                        ) : (
                            <Pencil16
                                color={
                                    isSelectedStepEditingDisabled
                                        ? colors.neutral400
                                        : colors.neutral800
                                }
                            />
                        )}
                    </SubMenuSelectButton>
                }
            >
                {EDIT_AUTOMATION_TOOLTIP}
            </HBTooltip>
        </ButtonContainer>
    );
};

export default observer(TemplateSelectButton);

const ButtonContainer = styled.div`
    &:not(:last-of-type) {
        margin-bottom: ${spacing[20]};
    }
    padding: ${spacing[12]} 0;

    &:last-of-type {
        padding-bottom: ${spacing[4]};
    }
`;

const ButtonLabel = styled(HBText)<{ hasError: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 0.5em;
    ${is('hasError')`
        color: ${colors.red50};
    `}
`;

const PreviewEdit = styled(HBText)`
    && {
        color: ${colors.blue20} !important; // To win over global colors for disabled sidebar (styles for SideBarContentContainer)
    }
`;

const SubMenuSelectButtonText = styled(HBText)`
    ${truncatedText}
    display: flex;
    gap: 4px;
`;

const SubMenuSelectButton = styled.button<{
    isActive: boolean;
    isDone: boolean;
    hasError: boolean;
}>`
    align-items: center;
    border-color: hsl(0, 0%, 80%);
    border-radius: 4px;
    border-style: solid;
    border-width: 0;
    box-shadow: none;
    display: flex;
    height: 36px;
    justify-content: space-between;
    outline: 0 !important;
    padding: 4px 8px 4px 12px;
    transition: all 100ms;
    width: 100%;
    color: ${colors.neutral900};
    cursor: pointer;
    background-color: ${colors.neutral100};
    &:hover,
    &:focus {
        background-color: ${colors.neutral200};
    }

    &:disabled {
        opacity: 1;
        border-width: 0;
        pointer-events: none;

        ${SubMenuSelectButtonText} {
            color: ${colors.neutral400};
        }
    }

    ${is('isDone')`
        &:hover, &:focus {
            background-color: ${colors.neutral100};
        }
    `}

    ${is('hasError')`
        background-color: ${colors.red50_01} !important;
        &:hover, &:focus {
            color: ${colors.neutral700};
        }
    `}

    ${is('isActive')`
        color: ${colors.neutral900} !important;
    `}
`;
